import { N40, N900, G50, G500 } from '@atlaskit/theme/colors';
import { messages } from '../messages';
import {
  OriginalEctlRecordProps,
  TransformedEctlRecordProps,
  TransformedEctlRecordType
} from '../models';

/*
  The values below are the ones that come back as data from the graphql, Hydra service.

  This is the current (Sep 2020) solution for >>translation<< of these data points, realizing
  that if there is ever a new data value for any of these (albeit very unlikely), we'd have to
  expand the below code to account for them.  This decision was made while balancing risk, MVP rollout,
  ROI, and diminishing returns if we tried a more elegant solution at this time.
*/
const LICENSE_TYPE_COMMERCIAL = 'COMMERCIAL';
const LICENSE_TYPE_ACADEMIC = 'ACADEMIC';
const INSTALL_TYPE_SERVER = 'SERVER';
const INSTALL_TYPE_DC = 'DATA CENTER';

const SIGNUP_QUERY_PARM_BUNDLE = 'bundle';
const SIGNUP_QUERY_PARM_MIGRATION_UUID = 'migrationSourceUuid';

/*
  below two consts are commented for now, per NOTE (1) below.
*/
// const NUMBER_SEATS_UNIT_USER = 'USER';
// const NUMBER_SEATS_UNIT_AGENT = 'AGENT';

export function transformEctlRecordData({
  originalEctlRecord,
  intl,
  activateTrialBaseUrl,
  activateUrlParameterKeys
}: OriginalEctlRecordProps & {
  activateTrialBaseUrl: string;
  activateUrlParameterKeys?: string[];
}) {
  const transformedEctlRecord: TransformedEctlRecordType = {
    name: '',
    installType: '',
    numberOfSeats: 0,
    numberOfSeatsUnit: '',
    purchaseType: '',
    supportEntitlementNumber: '',
    supportExpirationDate: '',
    ectlExpirationDate: '',
    available: false,
    migrationSourceUuid: '',
    bundleKey: ''
  };

  transformedEctlRecord.name = originalEctlRecord.name;
  transformedEctlRecord.supportEntitlementNumber =
    originalEctlRecord.entitlementKey;
  transformedEctlRecord.supportExpirationDate =
    originalEctlRecord.supportExpirationDate;
  transformedEctlRecord.ectlExpirationDate =
    originalEctlRecord.ectlExpirationDate;
  transformedEctlRecord.numberOfSeats = originalEctlRecord.numberOfSeats;

  transformedEctlRecord.available = originalEctlRecord.available;
  transformedEctlRecord.migrationSourceUuid =
    originalEctlRecord.migrationSourceUuid;
  transformedEctlRecord.bundleKey = originalEctlRecord.bundleKey;

  // ---------------------------
  const currentUrlParams = new URLSearchParams(window.location.search);
  const activateUrlParams = new URLSearchParams();

  activateUrlParams.set(
    SIGNUP_QUERY_PARM_BUNDLE,
    transformedEctlRecord.bundleKey
  );
  activateUrlParams.set(
    SIGNUP_QUERY_PARM_MIGRATION_UUID,
    transformedEctlRecord.migrationSourceUuid
  );

  activateUrlParams.set('source', 'WAC');

  activateUrlParameterKeys?.forEach((param) => {
    if (currentUrlParams.has(param)) {
      activateUrlParams.set(param, currentUrlParams.get(param) || '');
    }
  });

  transformedEctlRecord.activateTrialUrl = `${activateTrialBaseUrl}?${activateUrlParams.toString()}`;

  // ---------------------------
  let purchaseType = '';
  if (
    originalEctlRecord.licenseType.toUpperCase() === LICENSE_TYPE_COMMERCIAL &&
    originalEctlRecord.installType.toUpperCase() === INSTALL_TYPE_SERVER
  ) {
    purchaseType = intl.formatMessage(messages.purchaseTypeCommercialLic);
  } else if (
    originalEctlRecord.licenseType.toUpperCase() === LICENSE_TYPE_COMMERCIAL &&
    originalEctlRecord.installType.toUpperCase() === INSTALL_TYPE_DC
  ) {
    purchaseType = intl.formatMessage(messages.purchaseTypeCommercialSub);
  }
  if (
    originalEctlRecord.licenseType.toUpperCase() === LICENSE_TYPE_ACADEMIC &&
    originalEctlRecord.installType.toUpperCase() === INSTALL_TYPE_SERVER
  ) {
    purchaseType = intl.formatMessage(messages.purchaseTypeAcademicLic);
  } else if (
    originalEctlRecord.licenseType.toUpperCase() === LICENSE_TYPE_ACADEMIC &&
    originalEctlRecord.installType.toUpperCase() === INSTALL_TYPE_DC
  ) {
    purchaseType = intl.formatMessage(messages.purchaseTypeAcademicSub);
  }

  transformedEctlRecord.purchaseType = purchaseType;

  // ---------------------------
  let installType = '';
  if (originalEctlRecord.installType.toUpperCase() === INSTALL_TYPE_SERVER) {
    installType = intl.formatMessage(messages.installTypeServer);
  } else if (originalEctlRecord.installType.toUpperCase() === INSTALL_TYPE_DC) {
    installType = intl.formatMessage(messages.installTypeDc);
  }
  transformedEctlRecord.installType = installType;

  // ---------------------------
  /*
    TODO

    NOTE (1): The below, commented-out logic is correct, but we're currently (Sep 2020) using the TEMPORARY, 
    uncommented-out logic and will investigate, post-MVP, why hydra returns 'USER' for JSD user unit, 
    instead of returning 'AGENT'
  */
  // if (originalEctlRecord.numberOfSeatsUnit.toUpperCase() === NUMBER_SEATS_UNIT_USER) {
  //   numberOfSeatsUnit = intl.formatMessage(messages.numberOfSeatsUnitUsers);
  // }
  // else if (originalEctlRecord.numberOfSeatsUnit.toUpperCase() === NUMBER_SEATS_UNIT_AGENT) {
  //   numberOfSeatsUnit = intl.formatMessage(messages.numberOfSeatsUnitAgents);
  // }
  let numberOfSeatsUnit = intl.formatMessage(messages.numberOfSeatsUnitUsers);
  const regexValue = originalEctlRecord.name.match(
    /^([ ]{0,}jira[ ]{1,}service[ ]{1,}desk[ ]{0,})/gi
  );

  if (regexValue) {
    numberOfSeatsUnit = intl.formatMessage(messages.numberOfSeatsUnitAgents);
  }
  transformedEctlRecord.numberOfSeatsUnit = numberOfSeatsUnit;

  // ---------------------------
  transformedEctlRecord.supportEntitlementNumber = `SEN-${originalEctlRecord.entitlementKey}`;

  return transformedEctlRecord;
}

export function generateEctlExpirationMessage({
  transformedEctlRecord,
  intl,
  activateOneYearCloudTrial
}: TransformedEctlRecordProps) {
  /*
    NOTE: none of the below logic checks/validates the two dates' (supportExpirationDate & ectlExpirationDate)
    relationship, when they are both in the transformedEctlRecord payload.  No check/validation is made since
    they are what came back from the Hydra service.
  */

  let ectlExpirationMessage = '';

  const suppExpDateAsStr = transformedEctlRecord.supportExpirationDate; // is in string format at this pt.
  if (transformedEctlRecord.ectlExpirationDate) {
    const suppExpDateAsIntlDate = intl.formatDate(
      new Date(transformedEctlRecord.ectlExpirationDate)
    );
    ectlExpirationMessage = intl.formatMessage(messages.claimedEctlExpiresOn, {
      theDate: suppExpDateAsIntlDate
    });
  } else {
    const now = new Date();
    // set time to midnight, local time; b/c times from backend service are set to it, so better for date comparison
    now.setHours(0, 0, 0, 0);

    const suppExpDateAsDate = new Date(suppExpDateAsStr);
    let ectlExpDateAsDate;

    const aDayInMilliSecs = 24 * 60 * 60 * 1000;
    const aYearInMilliSecs = 365 * aDayInMilliSecs;

    const timeNumNow = now.getTime();
    const timeNumExp = suppExpDateAsDate.getTime();
    const timeDiffInDays = (timeNumExp - timeNumNow) / aDayInMilliSecs;

    if (suppExpDateAsDate < now || timeDiffInDays < 60) {
      ectlExpirationMessage = intl.formatMessage(
        messages.ectlExpiresInTwoMonths
      );
    } else {
      const milliSecsTillSuppExpDate: number =
        suppExpDateAsDate.valueOf() - now.valueOf();

      const SuppExpiresInAYearOrMore: boolean =
        milliSecsTillSuppExpDate >= aYearInMilliSecs;

      if (SuppExpiresInAYearOrMore) {
        // set ECTL expiration to a year from now
        ectlExpDateAsDate = now;
        ectlExpDateAsDate.setMilliseconds(
          ectlExpDateAsDate.getMilliseconds() + aYearInMilliSecs
        );
      } else if (activateOneYearCloudTrial) {
        // Promo expire one year from today
        ectlExpDateAsDate = new Date(Date.now() + aYearInMilliSecs);
      } else {
        // set ECTL expiration date equal to support expiration date.
        ectlExpDateAsDate = new Date(suppExpDateAsDate);
      }

      const ectlExpDateAsDateFmtd = intl.formatDate(ectlExpDateAsDate);

      ectlExpirationMessage = intl.formatMessage(messages.ectlWillLastUntil, {
        theDate: ectlExpDateAsDateFmtd
      });
    }
  }

  return ectlExpirationMessage;
}

/*
  Convenience function, for self-doc, readability, and future maintenance.
*/
export function getStyleForEctlElig(
  transformedEctlRecord: TransformedEctlRecordType
) {
  if (transformedEctlRecord.ectlExpirationDate) {
    return { backgroundColor: `${N40}`, color: `${N900}` }; // claimed ectl
  } else {
    return { backgroundColor: `${G50}`, color: `${G500}` };
  }
}
